import { AppData, Lightning, Router } from '@lightningjs/sdk';
import Page from 'components/Page';
import { PageId } from 'types/pageId';
import {
  ContentHub,
  Lane,
  LiveEventLaneItem,
  Promo,
  PromoChannel,
  PromoLane,
  Swimlanes,
} from 'services/cwData';
import BillboardCarousel from 'components/common/BillboardCarousel';
import CarouselSection, {
  SWIMLANES_HEIGHT,
} from 'components/pages/content-hub/CarouselSection';
import GridSection from 'components/pages/content-hub/GridSection';
import { Media } from 'types/api/media';
import { constants } from 'aliases';
import {
  getGenericContentHubBillboardViewContext,
  getGenericContentHubSwimlaneViewContext,
  StaticViewContexts,
} from 'types/analytics';
import { ContentHubSlugs } from 'types/contentHubs';
import { navigateToChannel, navigateToContentHub } from 'support/routerUtils';
import { isContentHub, isPromoChannel, isShow } from 'support/contentUtils';
import { NavigationItemLayout } from 'types/api/config';
import { ListItemContext, SelectItemContext } from 'types/events';
import {
  reportItemSelected,
  reportPromoSelected,
  reportScrollTracking,
  reportViewItemList,
  reportViewPromotion,
} from 'services/analytics/reportingServicePage';
import { ViewItemProps } from 'services/analytics/MParticle';
import ContentHubTitle from 'components/common/ContentHubTitle';
import { HoverableComponent } from 'components/common/HoverableComponent';
import { ErrorCause } from 'components/widgets/ErrorModal';
import { TileParentContext } from 'components/common/tiles/Tile';

const PADDING_X = constants.sizing.navbar.collapsedWidth;
const CONTENT_TOP_MARGIN = 120;
const CONTENT_OFFSET_Y = -200;
const LAYOUT_MARGIN_TOP = 16;
const X_PADDING_NO_NAVBAR = 160;
const X_PADDING__GRID_LAYOUT = 235;
const X_PADDING_CAROUSEL_LAYOUT = 118;
// mParticle Analytics
const QUARTILES = [0, 25, 50, 75, 100];

export interface ContentHubPageParams extends Router.PageParams {
  hubSlug?: string;
  nested?: boolean;
}

export type CollectionVisibilityItem = { obj: ViewItemProps };

export interface ContentHubPageTemplateSpec
  extends Lightning.Component.TemplateSpec {
  pageData: {
    swimlanes: Swimlanes;
    template: NavigationItemLayout;
    backgroundImage: string;
    backgroundColor: string;
  };
  hubSlug: string;
  nested: boolean;

  Content: {
    HeroBillboardCarousel: typeof HoverableComponent;
    Title: typeof ContentHubTitle;
    CarouselLayout: typeof CarouselSection;
    GridLayout: typeof GridSection;
  };
}

export default class ContentHubPage
  extends Page<ContentHubPageTemplateSpec>
  implements
    Lightning.Component.ImplementTemplateSpec<ContentHubPageTemplateSpec>
{
  private _hubSlug = '';
  private _hubTitle = '';
  nested = false;

  // Internal properties
  private _shouldUpdate = false;
  private _isBillboardFocusable = false;
  private _quartilesFired = 0;
  private _viewPromoTrackedIndices: number[] = [];
  private _selectedCrossIndex?: number = undefined;
  private _selectedMainIndex?: number = undefined;

  private _Content = this.getByRef('Content')!;
  private _HeroBillboardCarousel = this._Content.getByRef(
    'HeroBillboardCarousel',
  )!;
  private _Title = this._Content.getByRef('Title')!;
  private _CarouselLayout = this._Content.getByRef('CarouselLayout')!;
  private _GridLayout = this._Content.getByRef('GridLayout')!;

  get title() {
    return this._Title.text?.text ?? '';
  }

  get hubSlug() {
    return this._hubSlug;
  }

  override get pageId() {
    return this.hubSlug === ContentHubSlugs.HOME
      ? PageId.HOME
      : PageId.CONTENT_HUB;
  }

  static override _template(): Lightning.Component.Template<ContentHubPageTemplateSpec> {
    const LAYOUT_SIGNALS = {
      scrollToContent: '_scrollToContent',
      firstLaneScroll: '_firstLaneScroll',
      $onHover: '$onHover',
    };

    return {
      ...super._template(),
      Content: {
        w: 1920,
        h: 1080,
        flex: { direction: 'column' },
        // Workaround #1: We are using BillboardCarousel as a placeholder here because there seems to be a bug with LightningJS
        // where it doesn't render the other sections in the BillboardCarousel component when we update its `lane` field
        HeroBillboardCarousel: {
          type: HoverableComponent,
          h: BillboardCarousel.height + CONTENT_OFFSET_Y,
          visible: false,
          signals: {
            $onHover: '$onHover',
          },
        },
        Title: {
          type: ContentHubTitle,
          h: 60,
          x: PADDING_X,
          visible: false,
        },
        CarouselLayout: {
          type: CarouselSection,
          visible: false,
          signals: { ...LAYOUT_SIGNALS, onListFocus: '_onListFocus' },
        },
        GridLayout: {
          type: GridSection,
          visible: false,
          signals: { ...LAYOUT_SIGNALS, onGridFocus: '_onGridFocus' },
        },
      },
    };
  }

  override async _active() {
    // Call this first because it resets top nav
    super._active();

    this.updateTopNav();

    // reset Quartiles
    this._quartilesFired = 0;
    reportScrollTracking(0, this.pageId);
  }

  override _inactive() {
    super._inactive();
    this.sponsorBackground = '';
  }

  override _onUrlParams(params: ContentHubPageParams) {
    const hubSlug = params?.hubSlug ?? '';
    const nested = !!params?.nested;
    const currentState = this._getState();

    // We only update content hub page if:
    // 1. The hub slug is new, OR
    // 2. The nested state changed, OR
    // 3. The state is unset
    this._shouldUpdate =
      this._hubSlug !== hubSlug ||
      this.nested !== nested ||
      currentState === '';

    if (this._shouldUpdate) this._selectedCrossIndex = undefined;
    this._hubSlug = hubSlug;
    this.nested = nested;
  }

  override async _onDataProvided() {
    // this case would occur when going from one content hub page to another
    if (this.active) {
      this._active();
    }

    this._viewPromoTrackedIndices = [];
    this.isNavBarVisible = this.nested ? false : true;

    const { swimlanes, template, backgroundImage, backgroundColor } =
      this.pageData;

    // Background image (sponsor) -> background color (CMS) -> background color (app)
    this.sponsorBackground = backgroundImage;
    this.backgroundColor = backgroundColor ? backgroundColor : 'appBackground';

    // Only update content hub if we need to
    this._shouldUpdate ||= this.hasNewLiveEvents(swimlanes);

    if (this._shouldUpdate) {
      if (!swimlanes.lanes.length) {
        return this.showError();
      }

      this._hubTitle = swimlanes.title;
      this.updateTopNav();

      this.resetContentHub();
      this.buildBillboard(swimlanes, true);
      this.buildTitle(swimlanes);

      if (template === 'carousels') {
        // Note: There is additional 20px left padding within CarouselLayout
        // Position carousel before building lanes for nested layout dependencies
        this._CarouselLayout.patch({
          x: this.isNavBarVisible
            ? X_PADDING_CAROUSEL_LAYOUT
            : X_PADDING_NO_NAVBAR,
          isNavBarVisible: this.isNavBarVisible,
        });
        this.buildCarousels(swimlanes);
      } else if (template === 'grid') {
        this._GridLayout.patch({
          x: this.isNavBarVisible
            ? X_PADDING__GRID_LAYOUT
            : X_PADDING_NO_NAVBAR,
        });
        this.buildGrids(swimlanes);
      }

      if (this._isBillboardFocusable) {
        this._setState('Billboard');
      } else if (template === 'carousels') {
        this._setState('CarouselLayout');
      } else if (template === 'grid') {
        this._setState('GridLayout');
      }

      // Always scroll to the first lane when the page is updated
      this._firstLaneScroll();
    } else if (this._CarouselLayout.visible) {
      // always update continue lane

      const continueItems = swimlanes.lanes.find(
        lane => lane.laneType === 'continue-watching',
      ) as Lane | undefined;
      if (continueItems) this._CarouselLayout.updateContinueLane(continueItems);
    }

    // Set up visibilityService here instead of _active(),
    // otherwise reporting on page load won't work properly.
    this.setupCollectionVisibilityService();
  }

  private hasNewLiveEvents(newSwimlanes: Swimlanes) {
    const eventSlugs = (swimlanes: Swimlanes | null) =>
      (
        swimlanes?.lanes.find(lane => lane.laneType === 'events-list')
          ?.items as LiveEventLaneItem[] | undefined
      )?.map(item => item.slug);

    const currentSlugs = eventSlugs(this._CarouselLayout.data);
    const newSlugs = eventSlugs(newSwimlanes);

    if (!currentSlugs || !newSlugs) return false;

    return (
      currentSlugs.length !== newSlugs.length ||
      currentSlugs.some((slug, i) => slug !== newSlugs[i])
    );
  }

  private setupCollectionVisibilityService() {
    window.collectionVisibilityService.reset();

    const reportItems = (items: CollectionVisibilityItem[]) => {
      const products: ViewItemProps[] = items.map(item => item.obj);

      const isCarouselVisible = this._CarouselLayout.visible;

      // Report one impression event per row
      const rows = Array.from(
        new Set(products.map(product => product.mainIndex ?? 0)),
      );

      rows.forEach(row => {
        const rowProducts = products.filter(
          product => product.mainIndex === row,
        );

        const props = {
          row_name: isCarouselVisible ? rowProducts[0]?.rowTitle ?? '' : '',
          row_position: isCarouselVisible ? `${row + 1}` : '0',
        };
        reportViewItemList(rowProducts, props, this.pageId);
      });
    };
    window.collectionVisibilityService.initialize<ViewItemProps>(
      reportItems.bind(this),
    );
  }

  private updateIndices() {
    if (this._getState() !== 'CarouselLayout') return;

    const mainIndex = this._selectedMainIndex;
    const crossIndex = this._selectedCrossIndex;

    if (mainIndex !== undefined && crossIndex !== undefined) {
      this._CarouselLayout.setIndices(mainIndex, crossIndex);
    }

    if (mainIndex && mainIndex >= 1) {
      const swimlaneBottomY =
        -this._CarouselLayout.swimlaneWrapperY + SWIMLANES_HEIGHT;
      this._scrollToContent({
        index: mainIndex,
        scrollTarget: swimlaneBottomY,
      });
    }

    this._selectedMainIndex = undefined;
    this._selectedCrossIndex = undefined;
  }

  private showError() {
    this.widgets.errormodal.update({ cause: ErrorCause.UNAVAILABLE });
    AppData?.modalService.open('ErrorModal');
  }

  private buildBillboard(swimlanes: Swimlanes, isHero: boolean) {
    const promoLane = swimlanes.lanes.find(
      lane => lane.itemType === 'promo', // if we ever support billboard to tivo we will need to update this and onBillboardSelect args
    ) as PromoLane | undefined;
    if (!promoLane || !promoLane.items.length) return;

    const hasMultiplePromos = promoLane.items.length > 1;
    const hasPromoButton = !!promoLane.items[0]?.promoButton;

    this._isBillboardFocusable = hasMultiplePromos || hasPromoButton;
    this._HeroBillboardCarousel.visible = true;
    // Refer to Workaround #1
    this._HeroBillboardCarousel.children = [
      {
        type: BillboardCarousel,
        hasNavbar: this.isNavBarVisible,
        pageTitle: swimlanes.title,
        isHero, // this must be set before fade and lane
        lane: promoLane,
        signals: {
          $onHover: '$onHover',
        },
      },
    ];
  }

  private buildTitle(swimlanes: Swimlanes) {
    const { titleDisplayed, title } = swimlanes;
    const isHeroVisible = this._HeroBillboardCarousel.visible;
    const marginTop = isHeroVisible ? -this._Title.h : CONTENT_TOP_MARGIN;

    this._Title.patch({
      flexItem: { marginTop },
      visible: !!titleDisplayed,
      isBillboard: false,
      title: title,
    });
  }

  private buildCarousels(swimlanes: Swimlanes) {
    const isTitleVisible = this._Title.visible;
    const isHeroVisible = this._HeroBillboardCarousel.visible;
    const marginTop =
      isTitleVisible || isHeroVisible ? LAYOUT_MARGIN_TOP : CONTENT_TOP_MARGIN;

    this._CarouselLayout.patch({
      data: swimlanes,
      flexItem: { marginTop },
      visible: true,
    });
  }

  private buildGrids(swimlanes: Swimlanes) {
    const isTitleVisible = this._Title.visible;
    const isHeroVisible = this._HeroBillboardCarousel.visible;
    const marginTop =
      isTitleVisible || isHeroVisible ? LAYOUT_MARGIN_TOP : CONTENT_TOP_MARGIN;

    this._GridLayout.patch({
      data: swimlanes,
      flexItem: { marginTop },
      visible: true,
    });
  }

  private resetContentHub() {
    this._isBillboardFocusable = false;

    this._HeroBillboardCarousel.visible = false;
    this._CarouselLayout.visible = false;
    this._GridLayout.visible = false;

    this._HeroBillboardCarousel.alpha = 1;
    this._Content.y = 0;
  }

  private updateNavbar() {
    this.isNavBarVisible = this.nested ? false : true;

    // TODO: probably need to update this on EL-565
    this._Content.patch({
      x: this.isNavBarVisible ? constants.sizing.navbar.collapsedWidth : 0,
    });
  }

  private updateTopNav() {
    // Side bar nav items already show the user the title upon selection
    this.topNavTitle = this.nested ? this._hubTitle : '';
    this.topNavVisible = true;
  }

  private scrollToPromo() {
    this._Title.setSmooth('alpha', 1);
    this._Content.setSmooth('y', 0);

    this.topNavVisible = true;
  }

  private _scrollToContent(
    options: { index?: number; scrollTarget?: number } = {},
  ) {
    this.topNavVisible = false;

    const { index } = options;

    const isCarouselSectionVisible = this._CarouselLayout.visible;
    const isHeroBillboardVisible = this._HeroBillboardCarousel.visible;
    const isGridSectionVisible = this._GridLayout.visible;

    const isTitleVisible = this._Title.visible;
    let contentY = 0;

    if (isCarouselSectionVisible || isGridSectionVisible) {
      if (isCarouselSectionVisible) {
        const carouselSectionTopMargin =
          this._CarouselLayout.flexItem.marginTop ?? 0;
        contentY = 0 - carouselSectionTopMargin;
      }

      if (isHeroBillboardVisible) {
        // The title is inside the billboard, so there's no need to account for the title here
        const heroBillboardHeight = BillboardCarousel.height + CONTENT_OFFSET_Y;
        contentY -= heroBillboardHeight;
      } else if (isTitleVisible) {
        const titleTopMargin = this._Title.flexItem.marginTop ?? 0;
        const titleHeight = this._Title.h;
        contentY -= titleTopMargin + titleHeight;
      }

      this._Content.setSmooth('y', contentY);
    }

    if (isCarouselSectionVisible) {
      const { scrollTarget = 0 } = options;
      const amountCarouselScrolled =
        -contentY + scrollTarget - SWIMLANES_HEIGHT;

      // all scrollToContent calls from CarouselLayout guarantees index
      this._reportQuartile(amountCarouselScrolled, index!);
    }
  }

  private _firstLaneScroll(
    options: {
      scrollTarget?: number;
      laneHeight?: number;
    } = {},
  ) {
    const { scrollTarget = 0, laneHeight = 0 } = options;

    const shouldScrollToPromo =
      1080 - BillboardCarousel.height - laneHeight >= CONTENT_OFFSET_Y;

    if (shouldScrollToPromo) {
      // Note that we don't need to worry about reporting quartiles when scrolling to the promo (since we'd never scroll downwards)
      this.scrollToPromo();
    } else {
      this._scrollToContent({ index: 0, scrollTarget });
    }
  }

  private _onListFocus(index: number) {
    this.updateIndices();
  }

  private _onGridFocus(rowIndex: number) {
    if (rowIndex > 1) this.topNavVisible = false;
  }

  private _reportQuartile(amountScrolled: number, index: number) {
    if (this._hubSlug !== ContentHubSlugs.HOME) return;

    const adjustedCarouselLayoutHeight =
      this._CarouselLayout.scrollHeight - SWIMLANES_HEIGHT;

    const isHeroBillboardVisible = this._HeroBillboardCarousel.visible;
    const billboardH = isHeroBillboardVisible ? BillboardCarousel.height : 0;
    const totalScrollable = adjustedCarouselLayoutHeight + billboardH;
    const percentScrolled = amountScrolled / totalScrollable;

    for (const quartile of QUARTILES) {
      if (quartile > this._quartilesFired) {
        const percentQuartile = quartile / 100;
        if (percentScrolled >= percentQuartile) {
          this._quartilesFired = quartile;
          reportScrollTracking(quartile, this.pageId);
        } else {
          break;
        }
      }
    }
  }

  $onBillboardSelect(
    promo: Promo,
    isHero: boolean,
    imageUri: string,
    position: number | undefined,
  ) {
    if (isHero) {
      reportPromoSelected(
        {
          imageFileTitle: promo.promoImageId,
          index: position ?? 0,
          promo,
        },
        this.pageId,
      );
    }

    if (isContentHub(promo)) {
      this.onContentHubBillboardSelected(promo as ContentHub);
    } else if (isPromoChannel(promo)) {
      this.onChannelBillboardSelected(promo as PromoChannel);
    } else {
      this.onMediaBillboardSelected(promo as Media, imageUri, position);
    }
  }

  private onContentHubBillboardSelected(promo: ContentHub) {
    const { slug } = promo;
    navigateToContentHub(slug, { nested: true });
  }

  private onChannelBillboardSelected(promo: PromoChannel) {
    const { slug } = promo;
    const viewContext = this.getBillboardViewContext();
    navigateToChannel(slug, { viewContext });
  }

  private onMediaBillboardSelected(
    promo: Media,
    imageUri: string,
    position: number | undefined,
  ) {
    const action = isShow(promo) ? 'details' : 'play';
    const viewContext = this.getBillboardViewContext();

    const listItemContext: ListItemContext = {
      rowIndex: 0,
      itemIndex: position ?? 0,
      imageUrl: imageUri,
    };
    // super is used so we don't report row engagement for the hero
    super.$onTileSelected(promo, {
      action: action,
      viewContext,
      listItemContext,
    });
  }

  private getBillboardViewContext() {
    if (this._hubSlug === ContentHubSlugs.HOME) {
      return StaticViewContexts.HOME_BILLBOARD;
    } else {
      const hubTitle = this._hubTitle;
      const hubSlug = this._hubSlug;
      return getGenericContentHubBillboardViewContext(hubTitle || hubSlug);
    }
  }

  override $onTileSelected(
    data?: Media | ContentHub,
    context: SelectItemContext<TileParentContext> = {},
  ) {
    let rowTitle: string | undefined;
    let mainIndex: number | undefined;
    let crossIndex: number | undefined;

    if (!context.viewContext) {
      if (this._hubSlug === ContentHubSlugs.HOME) {
        if (context.parentContext?.isContinueSource) {
          context.viewContext = StaticViewContexts.HOME_CONTINUE_WATCHING;
        } else {
          context.viewContext = StaticViewContexts.HOME_EPISODES_SWIMLANES;
        }
      } else {
        const hubTitle = this._hubTitle;
        const hubSlug = this._hubSlug;
        context.viewContext = getGenericContentHubSwimlaneViewContext(
          hubTitle || hubSlug,
        );
      }
    }

    if (this._CarouselLayout.visible) {
      rowTitle =
        context.parentContext?.rowTitle ??
        this._CarouselLayout.currentRow.label;

      const carouselIndex = this._CarouselLayout.index;
      mainIndex = context.parentContext?.mainIndex ?? carouselIndex.mainIndex;
      crossIndex =
        context.parentContext?.crossIndex ?? carouselIndex.crossIndex;
      this._selectedMainIndex = mainIndex;
      this._selectedCrossIndex = crossIndex;
    }

    reportItemSelected(
      {
        imageUrl: context?.imageUrl,
        crossIndex,
        mainIndex,
        data,
        rowTitle,
        isTivoTileSource: !!context?.parentContext?.isTivoSource,
      },
      this.pageId,
    );

    if (context.action === 'play' || context.action === 'details') {
      context.listItemContext = {
        rowIndex: mainIndex ?? 0,
        itemIndex: crossIndex ?? 0,
        imageUrl: context?.imageUrl ?? '',
        rowTitle,
      };
    }

    super.$onTileSelected(data, context);
  }

  private $onTileFullyVisible(
    parentContext: TileParentContext,
    data: Media | ContentHub,
    imageUrl: string,
  ) {
    const { crossIndex, mainIndex, rowTitle, group, isTivoSource } =
      parentContext;

    const props: ViewItemProps = {
      crossIndex,
      imageUrl,
      mainIndex,
      data,
      rowTitle,
      isTivoTileSource: isTivoSource,
    };

    window.collectionVisibilityService.onFullyVisibleItem({
      row: mainIndex,
      col: crossIndex,
      group,
      obj: props,
    });
  }

  $onBillboardFullyVisible(promo: Promo, index: number) {
    if (this._viewPromoTrackedIndices.includes(index)) return;

    this._viewPromoTrackedIndices.push(index);
    reportViewPromotion({ promo, index }, this.pageId);
  }

  $onHover(target: unknown) {
    switch (target) {
      case this._CarouselLayout:
        this._setState('CarouselLayout');
        break;
      case this._GridLayout:
        this._setState('GridLayout');
        break;
      case this._HeroBillboardCarousel:
        if (this._isBillboardFocusable) {
          this._setState('Billboard');
        }
        break;
    }
  }

  static override _states() {
    return [
      class Billboard extends this {
        override $enter() {
          this.scrollToPromo();
        }

        override _getFocused(): Lightning.Component {
          // Refer to Workaround #1
          const billboards = this._HeroBillboardCarousel.childList.first;
          if (
            this._HeroBillboardCarousel.visible &&
            billboards &&
            (billboards as any).lane.items.length >= 1
          ) {
            return billboards as Lightning.Component;
          } else {
            return super._getFocused();
          }
        }

        override _handleDown() {
          if (this._CarouselLayout.visible) {
            // handle first row scroll here to avoid magic remote to trigger this scroll
            this._CarouselLayout.checkFirstRowScroll();
            this._setState('CarouselLayout');
          } else if (this._GridLayout.visible) {
            this._setState('GridLayout');
          }
        }
      },
      class CarouselLayout extends this {
        override _getFocused() {
          return this._CarouselLayout;
        }

        override _handleUp() {
          if (this._isBillboardFocusable) this._setState('Billboard');
          else super._handleUp();
        }

        override _handleBack() {
          // Scroll back up to top, focus on promos if exists, or first item in the content row
          if (this._isBillboardFocusable) {
            this._setState('Billboard');
            this._CarouselLayout.scrollToTop();
          } else if (this._CarouselLayout.scrollToTop()) {
            super._handleBack();
          }
        }
      },
      class GridLayout extends this {
        override _getFocused() {
          return this._GridLayout;
        }

        override _handleUp() {
          if (this._isBillboardFocusable) this._setState('Billboard');
          else super._handleUp();
        }

        override _handleBack() {
          // Scroll back up to top, focus on promos if exists, or first item in the grid.
          if (this._isBillboardFocusable) {
            this._setState('Billboard');
            this._GridLayout.scrollToTop();
          } else if (this._GridLayout.scrollToTop()) {
            super._handleBack();
          }
        }
      },
    ];
  }
}
